import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { getDisplayName } from '../../../../models/NameUtils';
import { useAdminAccountUsersQuery } from '../../../../queries/AdminTools/useAdminAccountUsersQuery';
import { useSupportUserSearchQuery } from '../../../../queries/Support/useSupportUserSearch';
import { getInitials } from '../../../../utils/Support/supportHelpers';
import { Button } from '../../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxList,
} from '../../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { Input } from '../../../../web/components-v2/Input';
import { Label } from '../../../../web/components-v2/Label';
import { supportSharedStyles as shared } from '../styles/supportSharedStyles';
import type { NewTicketDraftAccount, NewTicketDraftContact } from './newTicketDraft';
import { newTicketStyles as s } from './styles';

interface ContactOption extends NewTicketDraftContact {
	account?: NewTicketDraftAccount;
}

interface ContactFieldProps {
	account: NewTicketDraftAccount | null;
	contact: NewTicketDraftContact | null;
	manualContact: boolean;
	manualEmailError?: string;
	/** A picked contact can carry its own account (global search); the modal adopts it. */
	onChangeContact: (contact: NewTicketDraftContact | null, contactAccount?: NewTicketDraftAccount) => void;
	onToggleManual: (manual: boolean) => void;
}

const CONTACT_RESULTS_PAGE_SIZE = 8;

const getContactLabel = (contact: NewTicketDraftContact | null) => (contact ? contact.name || contact.email || '' : '');

/**
 * Contact picker for the new-ticket form. With an account selected it searches that account's
 * users (same endpoint as the account details Users tab); without one it falls back to the
 * support desk's global user search. Manual entry covers contacts that don't exist as users.
 */
export const ContactField: React.FC<ContactFieldProps> = ({
	account,
	contact,
	manualContact,
	manualEmailError,
	onChangeContact,
	onToggleManual,
}) => {
	const contactLabel = getContactLabel(contact);
	const [search, setSearch] = React.useState(contactLabel);
	const [syncedLabel, setSyncedLabel] = React.useState(contactLabel);
	// The input text is controlled here, so a contact set or cleared from outside (draft restore,
	// account switch) has to rewrite it.
	if (contactLabel !== syncedLabel) {
		setSyncedLabel(contactLabel);
		setSearch(contactLabel);
	}
	// The picked contact's own label in the input is not a search.
	const query = contact && search === contactLabel ? '' : search.trim();
	const debouncedQuery = useDebounceValue(query, 300);

	// The paginated endpoint is 0-based (page 0 is the first page).
	const accountUsersQuery = useAdminAccountUsersQuery({
		accountId: account?.id ?? '',
		page: 0,
		pageSize: CONTACT_RESULTS_PAGE_SIZE,
		statuses: [],
		roles: [],
		search: debouncedQuery,
	});
	const globalSearchQuery = useSupportUserSearchQuery(account ? '' : debouncedQuery);

	// The query keeps previous data as a placeholder across key changes, which is pleasant while
	// refining a search but dangerous across account switches: the old account's users would render
	// as clickable options. Dropping rows from other accounts closes that hole; same-account
	// placeholders (search refinement) pass through untouched.
	const options: ContactOption[] = account
		? (accountUsersQuery.data?.values ?? [])
				.filter(user => user.accountId === account.id)
				.map(user => ({
					userId: user.id,
					email: user.email,
					name: getDisplayName(user),
				}))
		: (globalSearchQuery.data ?? []).map(user => ({
				userId: user.userId,
				email: user.email,
				name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined,
				account: { id: user.accountId, name: user.accountName },
			}));
	const isSearching = account
		? accountUsersQuery.isLoading || accountUsersQuery.isFetching
		: !!debouncedQuery && globalSearchQuery.isLoading;
	const emptyMessage = isSearching
		? 'Searching contacts...'
		: account || debouncedQuery
			? 'No contacts found'
			: 'Type a name or email to search';

	if (manualContact) {
		return (
			<>
				<div>
					<Label styles={[shared.modalLabel]}>Contact email</Label>
					<Input
						styles={[shared.modalInput, manualEmailError && shared.modalInputError]}
						type='email'
						placeholder='customer@example.com'
						value={contact?.email ?? ''}
						onChange={e => onChangeContact({ ...contact, userId: undefined, email: e.target.value })}
					/>
					{manualEmailError && <div className={css(shared.modalErrorText)}>{manualEmailError}</div>}
				</div>
				<div>
					<Label styles={[shared.modalLabel]}>Contact name (optional)</Label>
					<Input
						styles={[shared.modalInput]}
						type='text'
						placeholder='John Doe'
						value={contact?.name ?? ''}
						onChange={e => onChangeContact({ ...contact, userId: undefined, name: e.target.value })}
					/>
				</div>
				<Button variant='link' styles={[s.manualLink]} onClick={() => onToggleManual(false)}>
					Back to contact search
				</Button>
			</>
		);
	}

	return (
		<>
			<Combobox
				items={options}
				value={contact}
				inputValue={search}
				onInputValueChange={setSearch}
				filter={null}
				itemToStringLabel={(option: ContactOption) => getContactLabel(option)}
				isItemEqualToValue={(option: ContactOption, value: NewTicketDraftContact) => option.userId === value.userId}
				onValueChange={(option: ContactOption | null) =>
					onChangeContact(
						option ? { userId: option.userId, email: option.email, name: option.name } : null,
						option?.account
					)
				}
			>
				<ComboboxInput
					aria-label='Contact'
					placeholder={account ? `Select a contact at ${account.name || 'this account'}...` : 'Select a contact...'}
					showClear={!!contact}
				/>
				<ComboboxContent>
					<ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
					<ComboboxList>
						{(option: ContactOption) => (
							<ComboboxAvatarItem key={option.userId} value={option}>
								<ComboboxAvatarItemAvatar fallbackText={getInitials(option.name, option.email)} />
								<ComboboxAvatarItemTitle>{option.name || option.email}</ComboboxAvatarItemTitle>
								<ComboboxAvatarItemDescription>
									{[option.name ? option.email : undefined, option.account?.name].filter(Boolean).join(' · ')}
								</ComboboxAvatarItemDescription>
							</ComboboxAvatarItem>
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
			{contact?.name && contact.email && <div className={css(s.csmRow)}>{contact.email}</div>}
			<Button variant='link' styles={[s.manualLink]} onClick={() => onToggleManual(true)}>
				Enter contact manually
			</Button>
		</>
	);
};
